import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import { type Session, fetchSession } from '../api/auth.ts';
import { isUnauthenticated } from '../api/client.ts';
import { FailureNotice, LoadingNotice } from '../components/AsyncState.tsx';
import { OrgBrandingSync } from '../theme/OrgBrandingSync.tsx';
import { SESSION_QUERY_KEY, SessionContext } from './sessionContext.ts';

/**
 * The route guard.
 *
 * Three outcomes, and keeping them apart is the whole job:
 *
 *   1. **Still checking** → render a status, redirect nothing. Redirecting before `/api/auth/me`
 *      answers would bounce every signed-in user to the login page on every hard refresh.
 *   2. **401** → not signed in. Redirect to `/login`, carrying where they were trying to go in
 *      router state (not a query parameter — see `redirectTarget.ts` for why that distinction is a
 *      security one).
 *   3. **Any other failure** → show it. A 404 because the endpoint does not exist yet, a shape
 *      mismatch, a 500, a dropped network: none of these mean "not signed in", and silently
 *      treating them as such produces a login page with no explanation and a user who thinks they
 *      have been logged out.
 *
 * The session is a TanStack Query, not a bespoke context with its own `useEffect` fetch, so that
 * an invalidation after login or logout is a cache operation rather than a second code path.
 */
export function RequireAuth({ children }: { readonly children: ReactNode }) {
  const { t } = useTranslation('common');
  const location = useLocation();
  const session = useQuery<Session>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => fetchSession(signal),
    // A 401 is an answer. Retrying it delays the redirect and does nothing else.
    retry: false,
    // The one query in the app that refetches on focus: a clinician coming back to a tab after an
    // idle timeout should find out immediately, not at their next click on a chart.
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });

  if (session.isPending) {
    return <LoadingNotice label={t('requireAuth.checkingSession')} />;
  }

  if (session.isError) {
    if (isUnauthenticated(session.error)) {
      return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
    }
    return (
      <main id="main" className="mx-auto w-full max-w-2xl p-4">
        <FailureNotice
          error={session.error}
          onRetry={() => {
            void session.refetch();
          }}
        />
      </main>
    );
  }

  return (
    <SessionContext.Provider value={session.data}>
      <OrgBrandingSync />
      {children}
    </SessionContext.Provider>
  );
}
